import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/axios';
import { useToast } from '../hooks/use-toast';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Play, Save, Loader2, Megaphone, Users, Clock } from 'lucide-react';

export default function CampaignCreator() {
  const { toast } = useToast();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  // Form State
  const [name, setName] = useState('');
  const [templateText, setTemplateText] = useState('');
  const [delaySecs, setDelaySecs] = useState(5);
  const [recipientsInput, setRecipientsInput] = useState('');

  // 1. Memorized Fetch Function (Safe from undefined errors)
  const fetchCampaigns = useCallback(async () => {
    setFetching(true);
    try {
      const { data } = await api.get('/campaigns');
      if (data.success) {
        setCampaigns(data.campaigns || []);
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: 'Failed to load campaigns', 
        variant: 'destructive' 
      });
    } finally {
      setFetching(false);
    }
  }, [toast]);

  // 2. Single Lifecycle Hook
  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // 3. Campaign Creation Logic
  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Clean up the input: split by comma or newline, remove whitespace, filter empties
      const parsedRecipients = recipientsInput
        .split(/[\n,]+/)
        .map(num => num.trim())
        .filter(num => num.length > 0);

      if (parsedRecipients.length === 0) {
        toast({ 
          title: 'Validation Error', 
          description: 'Please provide at least one valid recipient number.', 
          variant: 'destructive' 
        });
        setLoading(false);
        return;
      }

      const { data } = await api.post('/campaigns', {
        name,
        templateText,
        recipients: parsedRecipients,
        delaySecs: parseInt(delaySecs, 10)
      });

      if (data.success) {
        toast({ title: 'Success', description: 'Campaign drafted successfully!' });
        // Reset form
        setName('');
        setTemplateText('');
        setRecipientsInput('');
        setDelaySecs(5);
        fetchCampaigns();
      }
    } catch (error) {
      toast({
        title: 'Creation Failed',
        description: error.response?.data?.error || error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // 4. Campaign Launch Logic
  const handleLaunch = async (campaignId) => {
    try {
      const { data } = await api.post(`/campaigns/${campaignId}/launch`); 
      if (data.success) {
        toast({ title: 'Campaign Launched', description: data.message });
        fetchCampaigns(); // Refresh to show 'running' status
      }
    } catch (error) {
      toast({
        title: 'Launch Failed',
        description: error.response?.data?.error || 'Could not launch campaign. Check your balance.',
        variant: 'destructive'
      });
    }
  };

  // Helper for UI Badges
  const getStatusBadge = (status) => {
    switch (status) {
      case 'draft': 
        return <Badge variant="outline" className="text-gray-600 bg-gray-50 border-gray-200">Draft</Badge>;
      case 'running': 
        return <Badge className="bg-blue-100 text-blue-800 border-none hover:bg-blue-200">Running</Badge>;
      case 'completed': 
        return <Badge className="bg-green-100 text-green-800 border-none hover:bg-green-200">Completed</Badge>;
      case 'paused': 
        return <Badge variant="outline" className="text-yellow-600 bg-yellow-50 border-yellow-200">Paused</Badge>;
      case 'failed': 
        return <Badge variant="destructive">Failed</Badge>;
      default: 
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-8">
      
      {/* Header Section */}
      <div className="flex items-center gap-3 border-b pb-6">
        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <Megaphone className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Campaign Manager</h1>
          <p className="text-muted-foreground mt-1">Draft and launch bulk messaging campaigns safely.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* LEFT PANEL: CREATE CAMPAIGN FORM */}
        <Card className="col-span-1 h-fit shadow-sm border-t-4 border-t-blue-600">
          <CardHeader className="bg-gray-50/50 border-b">
            <CardTitle className="text-lg">Create New Campaign</CardTitle>
            <CardDescription className="text-xs">
              Messages are queued to protect your WhatsApp number from bans.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleCreateCampaign} className="space-y-5">
              
              <div className="space-y-1.5">
                <Label className="text-gray-700 font-semibold">Campaign Name</Label>
                <Input 
                  required 
                  value={name} 
                  onChange={(e) => setName(e.target.value)} 
                  placeholder="e.g., Summer Promo" 
                  className="bg-white"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-gray-700 font-semibold flex justify-between items-center">
                  <span>Message Template</span>
                </Label>
                <Textarea 
                  required 
                  value={templateText} 
                  onChange={(e) => setTemplateText(e.target.value)} 
                  placeholder="Hello! We have a special offer for you..." 
                  rows={4} 
                  className="bg-white resize-none"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-gray-700 font-semibold flex items-center gap-2">
                  <Users className="w-4 h-4 text-gray-400" />
                  Recipients List
                </Label>
                <Textarea 
                  required 
                  value={recipientsInput} 
                  onChange={(e) => setRecipientsInput(e.target.value)} 
                  placeholder="919876543210&#10;919876543211" 
                  rows={4} 
                  className="bg-white resize-none font-mono text-sm"
                />
                <p className="text-[10px] text-gray-400">Separate numbers with a comma or new line.</p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-gray-700 font-semibold flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  Delay (Seconds)
                </Label>
                <Input 
                  required 
                  type="number" 
                  min="2" 
                  max="60" 
                  value={delaySecs} 
                  onChange={(e) => setDelaySecs(e.target.value)} 
                  className="bg-white w-32"
                />
                <p className="text-[10px] text-amber-600 font-medium">
                  Recommended: 5-10s to avoid spam detection.
                </p>
              </div>

              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading}>
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Save as Draft
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* RIGHT PANEL: CAMPAIGN LIST */}
        <Card className="col-span-1 lg:col-span-2 shadow-sm">
          <CardHeader className="border-b">
            <CardTitle>Your Campaigns</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {fetching ? (
              <div className="p-8 flex justify-center items-center text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading campaigns...
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-gray-50/50">
                    <TableRow>
                      <TableHead className="px-6">Campaign Info</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead className="text-right px-6">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {campaigns.length > 0 ? (
                      campaigns.map((campaign) => (
                        <TableRow key={campaign.id} className="hover:bg-gray-50/50">
                          
                          <TableCell className="px-6 py-4">
                            <div className="font-semibold text-gray-900">{campaign.name}</div>
                            <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                <Users className="w-3 h-3" /> {campaign.total_count} recipients
                            </div>
                          </TableCell>
                          
                          <TableCell>
                            {getStatusBadge(campaign.status)}
                          </TableCell>
                          
                          <TableCell>
                            <div className="text-sm">
                              <span className="text-green-600 font-bold">{campaign.sent_count}</span> sent
                              <span className="text-gray-300 mx-1">|</span>
                              <span className="text-red-500 font-bold">{campaign.failed_count}</span> failed
                              
                              {/* Simple Progress Bar */}
                              <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                                <div 
                                    className="bg-blue-600 h-1.5 rounded-full" 
                                    style={{ width: `${(campaign.sent_count / (campaign.total_count || 1)) * 100}%` }}
                                ></div>
                              </div>
                            </div>
                          </TableCell>
                          
                          <TableCell className="text-right px-6">
                            {campaign.status === 'draft' && (
                              <Button 
                                size="sm" 
                                onClick={() => handleLaunch(campaign.id)} 
                                className="bg-green-600 hover:bg-green-700 text-white shadow-sm"
                              >
                                <Play className="h-4 w-4 mr-1" /> Launch
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan="4" className="text-center py-12 text-gray-400">
                          <div className="flex flex-col items-center justify-center">
                            <Megaphone className="w-10 h-10 mb-3 text-gray-300" />
                            <p>No campaigns created yet.</p>
                            <p className="text-sm">Use the form on the left to draft your first one.</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

      </div>
    </div>
  );
}